import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import { ORG_TYPES, BILL_BANDS, BRAND } from "@/lib/seed-content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  org_name: "",
  org_type: "",
  monthly_bill: "",
  address: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.org_type) e.org_type = "Please choose an organisation type";
    if (!form.monthly_bill) e.monthly_bill = "Please choose an estimated bill band";
    if (!form.address.trim() || form.address.trim().length < 6) e.address = "Please enter a full address";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within one working day.");
      setForm(initialState);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email us directly while we investigate.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="container-seed pt-16 pb-10 lg:pt-24 lg:pb-14">
          <FadeUp>
            <div className="text-overline">Book Assessment</div>
          </FadeUp>
          <FadeUp delay={1}>
            <h1 className="mt-4 font-display font-extrabold text-slate-900 tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
              A 30-minute conversation. A clear yes/no answer.
            </h1>
          </FadeUp>
          <FadeUp delay={2}>
            <p className="mt-5 text-slate-600 text-base sm:text-lg max-w-2xl leading-relaxed">
              Fill out the form below and we'll respond within one working day with next steps. No commitment,
              no follow-up sales pressure — just a straight assessment of whether SEED works for your building.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="container-seed section-pad" data-testid="contact-form-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm">
              {submitted ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="mt-6 font-display font-bold text-slate-900 text-2xl sm:text-3xl">Request received</h2>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto leading-relaxed">
                    Thank you. A member of the SEED team will be in touch within one working day with a clear
                    next step. No further action required from you.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-success-reset"
                    className="mt-8 inline-flex bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 px-5 rounded-lg text-sm"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-slate-900 font-semibold text-sm">Full name *</Label>
                      <Input
                        id="name"
                        data-testid="form-name"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="Jane Smith"
                        className="mt-2 h-11"
                        aria-invalid={!!errors.name}
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-600" data-testid="error-name">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-900 font-semibold text-sm">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="form-email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        placeholder="treasurer@yourclub.org.uk"
                        className="mt-2 h-11"
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="error-email">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone" className="text-slate-900 font-semibold text-sm">Phone (optional)</Label>
                      <Input
                        id="phone"
                        data-testid="form-phone"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        placeholder="07700 900000"
                        className="mt-2 h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="org_name" className="text-slate-900 font-semibold text-sm">Organisation name (optional)</Label>
                      <Input
                        id="org_name"
                        data-testid="form-org-name"
                        value={form.org_name}
                        onChange={(e) => setField("org_name", e.target.value)}
                        placeholder="Chichester RFC"
                        className="mt-2 h-11"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-900 font-semibold text-sm">Organisation type *</Label>
                      <Select value={form.org_type} onValueChange={(v) => setField("org_type", v)}>
                        <SelectTrigger data-testid="form-org-type" className="mt-2 h-11" aria-invalid={!!errors.org_type}>
                          <SelectValue placeholder="Select organisation type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ORG_TYPES.map((o) => (
                            <SelectItem key={o} value={o} data-testid={`org-option-${o.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.org_type && <p className="mt-1 text-xs text-red-600" data-testid="error-org-type">{errors.org_type}</p>}
                    </div>

                    <div>
                      <Label className="text-slate-900 font-semibold text-sm">Estimated monthly bill *</Label>
                      <Select value={form.monthly_bill} onValueChange={(v) => setField("monthly_bill", v)}>
                        <SelectTrigger data-testid="form-monthly-bill" className="mt-2 h-11" aria-invalid={!!errors.monthly_bill}>
                          <SelectValue placeholder="Select estimated band" />
                        </SelectTrigger>
                        <SelectContent>
                          {BILL_BANDS.map((b) => (
                            <SelectItem key={b} value={b} data-testid={`bill-option-${b.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.monthly_bill && <p className="mt-1 text-xs text-red-600" data-testid="error-monthly-bill">{errors.monthly_bill}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-slate-900 font-semibold text-sm">Building address *</Label>
                    <Textarea
                      id="address"
                      data-testid="form-address"
                      value={form.address}
                      onChange={(e) => setField("address", e.target.value)}
                      placeholder="Full postal address — including postcode"
                      rows={3}
                      className="mt-2"
                      aria-invalid={!!errors.address}
                    />
                    {errors.address && <p className="mt-1 text-xs text-red-600" data-testid="error-address">{errors.address}</p>}
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-900 font-semibold text-sm">Anything else? (optional)</Label>
                    <Textarea
                      id="message"
                      data-testid="form-message"
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      placeholder="Tell us about your roof, recent bills, governance considerations..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="form-submit"
                      className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400/60 text-white font-semibold py-4 px-6 rounded-lg group"
                    >
                      {submitting ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>
                      ) : (
                        <>Submit Assessment Request <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>
                      )}
                    </button>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      By submitting you agree to be contacted by SEED regarding your enquiry. We do not share
                      your information.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* SIDE INFO */}
          <div className="lg:col-span-4">
            <div className="navy-panel grain text-white rounded-2xl p-8 sticky top-28">
              <div className="text-overline text-emerald-400">Speak to us directly</div>
              <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">Prefer email or phone?</h3>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-start gap-3 text-slate-200">
                  <Mail className="w-4 h-4 mt-0.5 text-emerald-400 flex-none" />
                  <a href={`mailto:${BRAND.contactEmail}`} className="hover:text-white" data-testid="contact-email-link">{BRAND.contactEmail}</a>
                </li>
                <li className="flex items-start gap-3 text-slate-200">
                  <Phone className="w-4 h-4 mt-0.5 text-emerald-400 flex-none" />
                  <a href={`tel:${BRAND.contactPhone.replace(/\s/g, "")}`} className="hover:text-white" data-testid="contact-phone-link">{BRAND.contactPhone}</a>
                </li>
                <li className="flex items-start gap-3 text-slate-200">
                  <MapPin className="w-4 h-4 mt-0.5 text-emerald-400 flex-none" />
                  <span>{BRAND.contactAddress}</span>
                </li>
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10 text-xs text-slate-400 leading-relaxed">
                Typical response time: <span className="text-white font-semibold">under 24 hours</span>, Monday to Friday.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
